import { minBy, range } from "lodash-es";

import { internalActions } from "@/action/internalActions";
import { windowFromRelative } from "@/math/coordinate-transform";
import { scrollTo, visibleWindowSize } from "@/math/coordinate-utils";
import {
  applyCoord,
  clampCoord,
  distance2,
  divided,
  interpolate,
  maxCoord,
  minCoord,
  minus,
  plus,
  rectangleSize,
  sumCoord,
  times,
} from "@/math/coordinates";
import { boardAspect, maxZoom, regionZoomDelay, regionZoomSteps } from "@/model/Settings";
import type { Board, ZoomedRectangle } from "@/model/board";
import type { PositionedCard } from "@/model/card";
import type { Rectangle, RelativeCoordinate, WindowCoordinate } from "@/model/coordinates";
import { initWindowCoord, relativeCoord } from "@/model/coordinates";
import { halfCardSize, useBoardStore } from "@/store/board";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useZoomStore } from "@/store/zoom";

import { centerCoord, viewportFromWindow, windowFromViewport } from "./dom/coordinates";

export function arrangeCards(
  board: Board,
  cards: PositionedCard[],
  bounds: Rectangle<RelativeCoordinate>,
): PositionedCard[] {
  const gapFactor = useClientSettingsStore().initialLineupDistance / 100;
  const gapCardSize = times(board.cardSize, gapFactor);

  const field = rectangleSize(bounds);

  let factor = 1;
  let cardSize = gapCardSize;
  let count = applyCoord(divided(field, cardSize), Math.floor);
  while (count.x * count.y < cards.length) {
    factor = field.x / (count.x + 1.1) / gapCardSize.x;
    cardSize = times(gapCardSize, factor);
    count = applyCoord(divided(field, cardSize), Math.floor);
  }
  internalActions.setCardSize(board.id, board.cardSize.factor * factor);
  const base = plus(bounds.p0, divided(cardSize, 2));
  return moveCards(cards, (_, index) =>
    plus(base, times(cardSize, index % count.x, Math.floor(index / count.x))),
  );
}

export function alignCards(
  cards: PositionedCard[],
  direction: "horizontal" | "vertical",
): PositionedCard[] {
  const sum = sumCoord(cards.map((card) => card.meta.pos));
  return cards.map((card) => {
    const pos =
      direction === "horizontal"
        ? relativeCoord(card.meta.pos.x, sum.y / cards.length)
        : relativeCoord(sum.x / cards.length, card.meta.pos.y);
    return { data: { id: card.data.id }, meta: { pos } };
  });
}

export function distributeCards(
  cards: PositionedCard[],
  direction: "horizontal" | "vertical",
): PositionedCard[] {
  const comp = direction === "horizontal" ? "x" : "y";
  const sorted = [...cards].sort((a, b) => a.meta.pos[comp] - b.meta.pos[comp]);
  const start = sorted[0].meta.pos[comp];
  const end = sorted[sorted.length - 1].meta.pos[comp];
  const dist = (end - start) / (cards.length - 1);
  return sorted.map((card, index) => {
    const pos =
      direction === "horizontal"
        ? relativeCoord(start + dist * index, card.meta.pos.y)
        : relativeCoord(card.meta.pos.x, start + dist * index);
    return { data: { id: card.data.id }, meta: { pos } };
  });
}

export function lineupCards(
  cards: PositionedCard[],
  cardSize: RelativeCoordinate,
  cardDistance: number,
  regionOf: (pos: RelativeCoordinate) => Rectangle<RelativeCoordinate>,
): PositionedCard[] {
  const bounds = boundsOf(cards);
  const columns = similarColumnCount(cards, cardSize, bounds);
  const region = regionOf(bounds.min);
  return moveCards(cards, (_, index) => {
    const pos = plus(
      bounds.min,
      relativeCoord(
        cardSize.x * cardDistance * (index % columns),
        cardSize.y * cardDistance * Math.floor(index / columns),
      ),
    );
    return assureInsideRegion(pos, region, cardSize);
  });
}

function similarColumnCount(
  cards: PositionedCard[],
  cardSize: RelativeCoordinate,
  bounds: { min: RelativeCoordinate; max: RelativeCoordinate },
) {
  const aspectOfBounds =
    ((cardSize.x + bounds.max.x - bounds.min.x) / (cardSize.y + bounds.max.y - bounds.min.y)) *
    boardAspect;
  return minBy(range(1, cards.length + 1), (cols) => {
    const aspect = cols / Math.ceil(cards.length / cols);
    return Math.abs(aspectOfBounds - aspect);
  })!;
}

export function stackCards(
  cards: PositionedCard[],
  cardSize: RelativeCoordinate,
  cardDistance: RelativeCoordinate,
  regionOf: (pos: RelativeCoordinate) => Rectangle<RelativeCoordinate>,
): PositionedCard[] {
  const bounds = boundsOf(cards);
  const region = regionOf(bounds.min);
  return moveCards(cards, (_, index) => {
    const pos = plus(
      bounds.min,
      relativeCoord(
        cardSize.x * cardDistance.x * (index - (cardDistance.x < 0 ? cards.length - 1 : 0)),
        cardSize.y * cardDistance.y * (index - (cardDistance.y < 0 ? cards.length - 1 : 0)),
      ),
    );
    return assureInsideRegion(pos, region, cardSize);
  });
}

function assureInsideRegion(
  pos: RelativeCoordinate,
  region: Rectangle<RelativeCoordinate>,
  cardSize: RelativeCoordinate,
) {
  const halfCard = divided(cardSize, 2);
  return clampCoord(pos, plus(region.p0, halfCard), minus(region.p1, halfCard));
}

function boundsOf(cards: PositionedCard[]) {
  const pos = cards.map((card) => card.meta.pos);
  return { min: minCoord(pos, relativeCoord(1, 1)), max: maxCoord(pos, relativeCoord(0, 0)) };
}

function moveCards(
  cards: PositionedCard[],
  calcNewPos: (card: PositionedCard, index: number) => RelativeCoordinate,
): PositionedCard[] {
  const movedCards = new Set<string>();
  return cards.map((card, index) => {
    const newPos = calcNewPos(card, index);
    const nearest = nearestCard(newPos);
    return { data: { id: nearest.data.id }, meta: { pos: newPos } };
  });

  function nearestCard(pos: RelativeCoordinate) {
    let minDist = 10;
    let nearest = cards[0];
    cards.forEach((card) => {
      if (!movedCards.has(card.data.id)) {
        const dist = distance2(card.meta.pos, pos);
        if (dist < minDist) {
          minDist = dist;
          nearest = card;
        }
      }
    });
    movedCards.add(nearest.data.id);
    return nearest;
  }
}

export function zoomToRegion(bounds: Rectangle<RelativeCoordinate>, adjustZoom = 0.95) {
  const targetSize = minus(windowFromRelative(bounds.p1), windowFromRelative(bounds.p0));
  const windowSize = visibleWindowSize();
  const zooms = divided(windowSize, targetSize.x, targetSize.y);
  const startZoom = Math.min(maxZoom - 0.1, useZoomStore().factor); // assure that there's always some zoom
  const endZoom = Math.min(maxZoom, startZoom * adjustZoom * Math.min(zooms.x, zooms.y));
  const regionCenter = divided(plus(bounds.p0, bounds.p1), 2);
  new RegionScroller(startZoom, endZoom, regionCenter).start();
}

class RegionScroller {
  static step = 1 / regionZoomSteps;
  static maxT = 1 - RegionScroller.step / 2;
  t = 0;
  startDist: WindowCoordinate;

  constructor(
    private startZoom: number,
    private endZoom: number,
    private regionCenter: RelativeCoordinate,
  ) {
    this.startDist = this.centerDist();
  }

  start() {
    // zoom store will call scroll whenever dynamic zoom factor changes
    useZoomStore().startZoom(this);
    this.t = 0;
    this.zoomStep();
  }

  zoomStep() {
    this.t += RegionScroller.step;
    // interpolate zoom exponential
    const zoom = this.startZoom * Math.pow(this.endZoom / this.startZoom, this.t);
    useZoomStore().setDynamicFactor(zoom, true);

    if (this.t < RegionScroller.maxT) {
      setTimeout(() => this.zoomStep(), regionZoomDelay);
    } else {
      useZoomStore().endZoom();
    }
  }

  scroll(board: ZoomedRectangle) {
    // interpolate the distance between viewport center and region center from the initial distance down to 0
    const targetDist = interpolate(this.t, this.startDist, initWindowCoord());
    const effectiveDist = this.centerDist({ board });
    scrollTo(targetDist, viewportFromWindow(effectiveDist));
  }

  centerDist(board?: { board: ZoomedRectangle }) {
    return minus(windowFromViewport(centerCoord()), windowFromRelative(this.regionCenter, board));
  }
}

export function findFreePosition(
  coord: RelativeCoordinate,
  step: RelativeCoordinate,
): RelativeCoordinate {
  const minDist = Math.min(step.x, step.y);
  const halfCard = halfCardSize();
  const min = halfCard;
  const max = minus(relativeCoord(1, 1), halfCard);
  let c = coord;
  const cards = useBoardStore().currentBoard().cards;
  let notFree;
  do {
    notFree = false;
    for (const id in cards) {
      const card = cards[id];
      if (distance2(card.meta.pos, c) < 0.9 * minDist * minDist) {
        notFree = true;
        // Position 'c' already has a sticky -> adjust by step
        c = clampCoord(plus(c, step), min, max);
        break;
      }
    }
  } while (notFree && (c.x > min.x || c.y > min.y) && (c.x < max.x || c.y < max.y));
  return c;
}
